import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Package, ChefHat, Store, Bike, Home, ExternalLink, RefreshCw, Loader } from 'lucide-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import { CONTACT } from '../mock';

const STEPS_PICKUP = [
  { key: 'queued', label: 'Order Received', icon: Package },
  { key: 'in_kitchen', label: 'In the Kitchen', icon: ChefHat },
  { key: 'ready', label: 'Ready for Pickup', icon: Store },
  { key: 'completed', label: 'Picked Up', icon: CheckCircle2 },
];
const STEPS_DELIVERY = [
  { key: 'queued', label: 'Order Received', icon: Package },
  { key: 'in_kitchen', label: 'In the Kitchen', icon: ChefHat },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Bike },
  { key: 'completed', label: 'Delivered', icon: Home },
];

export default function TrackOrder() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/track`);
      setData(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Order not found.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (loading) return (
    <main><PageHeader title="Track Order" breadcrumb="Tracking" />
      <div className="py-24 flex items-center justify-center gap-2 text-gray-500"><Loader size={16} className="animate-spin" /> Loading your order...</div>
    </main>
  );

  if (error) return (
    <main><PageHeader title="Track Order" breadcrumb="Tracking" />
      <div className="py-24 text-center"><p className="text-gray-600 mb-4">{error}</p><Link to="/menu" className="btn-wrappus text-sm">Browse Menu</Link></div>
    </main>
  );

  const steps = data.type === 'delivery' ? STEPS_DELIVERY : STEPS_PICKUP;
  let currentIndex = steps.findIndex((s) => s.key === data.kitchenStatus);
  if (currentIndex < 0) currentIndex = 0;

  return (
    <main>
      <PageHeader title={`Hi ${data.customerFirstName || 'there'}!`} breadcrumb="Tracking" />
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-widest">Order #{data.id.slice(0, 8)}</div>
              <h1 className="font-heading font-bold text-2xl text-[#0F1C2E] mt-1">{data.kitchenLabel}</h1>
              {data.scheduledFor && (
                <div className="text-sm text-[#ED2E2E] font-semibold mt-1">⏰ Scheduled for {new Date(data.scheduledFor).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</div>
              )}
            </div>
            <button onClick={load} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#ED2E2E]"><RefreshCw size={14} /> Refresh</button>
          </div>

          {/* Timeline */}
          <div className="bg-[#FDF3EC] rounded-3xl p-8 mb-6">
            <div className="relative">
              <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full" />
              <div className="absolute top-6 left-0 h-1 bg-[#ED2E2E] rounded-full transition-all duration-500" style={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }} />
              <div className="relative grid grid-cols-4 gap-2">
                {steps.map((s, i) => {
                  const done = i <= currentIndex;
                  const active = i === currentIndex;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${done ? 'bg-[#ED2E2E] text-white shadow-lg' : 'bg-white text-gray-400 border-2 border-gray-200'} ${active ? 'scale-110 ring-4 ring-[#ED2E2E]/20' : ''}`}>
                        <Icon size={20} />
                      </div>
                      <div className={`mt-2 text-xs font-semibold ${done ? 'text-[#0F1C2E]' : 'text-gray-400'}`}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {data.type === 'delivery' && data.deliveryTracking && (
            <a href={data.deliveryTracking} target="_blank" rel="noopener noreferrer" className="block bg-[#0F1C2E] text-white rounded-2xl p-5 mb-6 hover:bg-[#1a2b45] transition">
              <div className="flex items-center justify-between">
                <div><div className="text-xs uppercase tracking-widest text-white/60">Live courier tracking</div><div className="font-heading font-bold text-lg mt-1">Open Uber Direct Map</div></div>
                <ExternalLink size={20} />
              </div>
            </a>
          )}

          {data.type === 'pickup' && data.kitchenStatus === 'ready' && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6">
              <div className="font-heading font-bold text-green-700 mb-1">Your order is ready!</div>
              <p className="text-sm text-gray-700">Head over to <strong>{CONTACT.address}</strong>. Please bring your order number: <strong>#{data.id.slice(0, 8)}</strong></p>
            </div>
          )}

          <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6">
            <h3 className="font-heading font-bold text-lg mb-3">Your Order</h3>
            <div className="space-y-2 text-sm">
              {data.items?.map((i, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-gray-700">{i.qty}× {i.name}</span>
                  <span className="font-semibold">${(i.price * i.qty).toFixed(2)}</span>
                </div>
              ))}
              <div className="pt-3 border-t border-gray-100 mt-3 flex justify-between font-bold text-[#0F1C2E]">
                <span>Total</span><span className="text-[#ED2E2E]">${data.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={`/reorder/${data.id}`} className="btn-wrappus text-sm flex-1 justify-center">Reorder in 1 Click</Link>
            <Link to="/menu" className="flex-1 justify-center inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#ED2E2E] text-[#ED2E2E] font-semibold hover:bg-[#FDF3EC] transition">Browse Menu</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
